import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMountTransition } from '../hooks/useMountTransition';
import { activateClose, dismissFocus } from '../utils/dismissFocus';
import { isNativeApp } from '../utils/platform';
import { openExternalUrl } from '../utils/openExternalUrl';
import './styles/ConfirmDialog.css';

/**
 * Кастомный диалог подтверждения (вместо window.confirm).
 * Рендерится в document.body, чтобы быть поверх всего UI.
 */
const ConfirmDialog = ({
  open,
  title = 'Переход на другой сайт',
  message,
  href = '',
  confirmLabel = 'Перейти',
  cancelLabel = 'Отмена',
  onConfirm,
  onCancel,
}) => {
  const { mounted, active } = useMountTransition(open, 320);

  const handleCancel = useCallback(
    (event) => {
      dismissFocus(event);
      onCancel?.();
    },
    [onCancel]
  );

  const handleConfirm = (event) => {
    dismissFocus(event);
    onConfirm?.();
  };

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [mounted, handleCancel]);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className={`kkt-blur-backdrop${active ? ' is-visible' : ''}`}
        onClick={handleCancel}
        role="presentation"
      />
      <div
        className={`confirm-dialog${active ? ' is-visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="confirm-dialog__icon" aria-hidden="true">
          <i className="bi bi-box-arrow-up-right"></i>
        </div>
        <h2 id="confirm-dialog-title" className="confirm-dialog__title">
          {title}
        </h2>
        {message ? <p className="confirm-dialog__text">{message}</p> : null}
        <div className="confirm-dialog__actions">
          <button
            type="button"
            className="confirm-dialog__btn confirm-dialog__btn--ghost"
            onPointerDown={(event) => activateClose(event, handleCancel)}
            onClick={(event) => activateClose(event, handleCancel)}
          >
            {cancelLabel}
          </button>
          {href ? (
            <a
              className="confirm-dialog__btn confirm-dialog__btn--primary"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                if (isNativeApp() && href) {
                  event.preventDefault();
                  openExternalUrl(href);
                }
                dismissFocus(event);
                onConfirm?.();
              }}
            >
              {confirmLabel}
            </a>
          ) : (
            <button
              type="button"
              className="confirm-dialog__btn confirm-dialog__btn--primary"
              onPointerDown={(event) => activateClose(event, handleConfirm)}
              onClick={(event) => activateClose(event, handleConfirm)}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default ConfirmDialog;
